import type { RcFile } from 'antd/es/upload/interface';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { IMAGE_TYPE, MEDIA_TYPE, STATUS_LOADED, STATUS_LOADING } from 'constants/MediaConstants';
import type { AbstractMedia } from 'modules/medias/types/abstract-media';
import { uniq } from 'utils/ObjectUtils';

export const DISPLAY_TYPES = {
  COMPUTER: 'COMPUTER',
  LIBRARY: 'LIBRARY',
  TAKE_PICTURE: 'TAKE_PICTURE',
} as const;

export type DisplayTypeEnum = (typeof DISPLAY_TYPES)[keyof typeof DISPLAY_TYPES];

export const DISPLAY_TYPES_NAME: Record<DisplayTypeEnum, React.ReactNode> = {
  [DISPLAY_TYPES.COMPUTER]: <FormattedMessage id="mediaLibrary.fromComputer" defaultMessage="From computer" />,
  [DISPLAY_TYPES.LIBRARY]: <FormattedMessage id="mediaLibrary.fromLibrary" defaultMessage="From library" />,
  [DISPLAY_TYPES.TAKE_PICTURE]: <FormattedMessage id="mediaLibrary.fromCamera" defaultMessage="From camera" />,
};

type FormattedMedia = {
  file: RcFile;
  id: string | number;
  name: string;
  isFavorite: boolean;
  addToMediaLibrary: boolean;
  filename: string;
  path: string;
  mediaType: string;
  type: string;
  preRecorded?: boolean;
};

export const format = (file: RcFile, uploadInLibrary?: boolean, preRecorded?: boolean): FormattedMedia => ({
  file,
  id: file.uid ?? Date.now(),
  name: file.name.split('.').slice(0, -1).join('.'),
  isFavorite: false,
  addToMediaLibrary: uploadInLibrary ?? false,
  filename: file.name,
  path: URL.createObjectURL(file),
  mediaType: (MEDIA_TYPE as Record<string, string>)[file.type.split('/')[0].toUpperCase()] || MEDIA_TYPE.DOCUMENT,
  type: file.type,
  preRecorded,
});

type GalleryMedia = {
  data: AbstractMedia;
  status: string;
  isUploaded: boolean;
  uploadedId: string;
};

export const formatToGallery = (medias: AbstractMedia[] = []): GalleryMedia[] =>
  medias.map(media => ({
    data: media,
    status: media.status ?? STATUS_LOADED,
    isUploaded: true,
    uploadedId: media.uploadId ?? media.id,
  }));

type MediaWithData = {
  data?: { type?: string; transformedPath?: string };
  type?: string;
  transformedPath?: string;
};

export const getMediasTypes = (medias: MediaWithData[] = []): string[] =>
  uniq(medias.map(({ data, type }) => data?.type ?? type)).sort();

export const hasOnlyOriginalImages = (medias: MediaWithData[]): boolean =>
  medias
    .filter(({ data, type }) => (data?.type ?? type) === IMAGE_TYPE)
    .every(media => ('data' in media ? !media.data?.transformedPath : !media.transformedPath));

export const DEFAULT_LOADING_MEDIA = { status: STATUS_LOADING, isUploaded: false, data: {} };

export const NUMBER_MAX_FILES = 20;
